import { proxy } from 'valtio'
import { useSnapshot } from 'valtio/react'

const selectionDraggingStore = proxy({ dragging: false })

export function useSelectionDragging() {
  return useSnapshot(selectionDraggingStore).dragging
}

// 节点按下状态：用于在「选中 → 拖拽开始」的空窗期隐藏 toolbar，避免快速拖动时闪烁
const nodePressingStore = proxy({ pressing: false })

export function resetPointerPressedState() {
  selectionDraggingStore.dragging = false
  nodePressingStore.pressing = false
}

export function useNodePressing() {
  return useSnapshot(nodePressingStore).pressing
}

// 在 capture 阶段监听 window 指针事件，命中节点时按需置位，松开/取消时清除。
// 返回清理函数，供 useEffect 卸载时移除监听。
export function bindNodePressing() {
  // 按下已选中节点时先不隐藏，仅“武装”拖动检测；移动超过阈值才视为拖动并隐藏。
  // 这样：纯点击已选中节点不闪（始终可见），拖动已选中节点在拖拽前隐藏（不闪）。
  // 阈值用于忽略点击时的轻微抖动，避免误判为拖动。
  const DRAG_THRESHOLD = 3
  let armed = false
  let downX = 0
  let downY = 0

  const handleDown = (e: PointerEvent) => {
    const target = e.target as Element | null
    // 仅当按在节点上时处理；toolbar 按钮在 portal 内、不在 .react-flow__node 内，不受影响
    const nodeEl = target?.closest('.react-flow__node')
    if (!nodeEl) {
      return
    }
    // capture 阶段早于 React Flow 应用 selected class，故新按下的未选中节点此刻尚无 .selected。
    if (nodeEl.classList.contains('selected')) {
      // 已选中：可能是点击或拖动，先武装，交给 pointermove 判定
      armed = true
      downX = e.clientX
      downY = e.clientY
    } else {
      // 未选中：即将新选中，立即隐藏以避免 toolbar 在拖拽开始前闪现
      nodePressingStore.pressing = true
    }
  }
  const handleMove = (e: PointerEvent) => {
    if (!armed) {
      return
    }
    if (Math.abs(e.clientX - downX) > DRAG_THRESHOLD || Math.abs(e.clientY - downY) > DRAG_THRESHOLD) {
      armed = false
      nodePressingStore.pressing = true
    }
  }
  const handleUp = () => {
    armed = false
    nodePressingStore.pressing = false
  }
  const handleReset = () => {
    armed = false
    resetPointerPressedState()
  }

  window.addEventListener('pointerdown', handleDown, true)
  window.addEventListener('pointermove', handleMove, true)
  window.addEventListener('pointerup', handleUp, true)
  window.addEventListener('pointercancel', handleUp, true)
  window.addEventListener('blur', handleReset)

  return () => {
    window.removeEventListener('pointerdown', handleDown, true)
    window.removeEventListener('pointermove', handleMove, true)
    window.removeEventListener('pointerup', handleUp, true)
    window.removeEventListener('pointercancel', handleUp, true)
    window.removeEventListener('blur', handleReset)
  }
}

// 框选开始时调用（对应 ReactFlow onSelectionStart）
export function startSelectionDragging() {
  selectionDraggingStore.dragging = true
}

// 框选结束时调用（对应 ReactFlow onSelectionEnd）
export function endSelectionDragging() {
  selectionDraggingStore.dragging = false
}
